import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Correspondant } from 'src/app/utils/interfaces/new/correspondant.interface';
import { CorrespondantFilter } from 'src/app/utils/interfaces/new/filtre-correspondant.interface';
import { SortEnum } from 'src/app/utils/enums/sort-enum';
import { FiltresService } from '../filtres/filtres.service';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { environment } from 'src/environments/environment';
import { SnackbarService } from '../snackbar/snackbar.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Facture, FactureCreate, FactureUpdate, OrigineFacture, FacturesList } from 'src/app/utils/interfaces/new/Facture';
import { AddPaiementRequest, AddPaiementResponse, FactureMeta, ListPaiementsResponse } from 'src/app/utils/interfaces/new/recettes-paiements.models';
import { PaiementItem } from 'src/app/utils/interfaces/new/PaiementItem';
import Integer from '@zxing/library/esm/core/util/Integer';
import { BooleanLiteral } from 'typescript';

export interface FactureSums {
  montantTotal: number;
  montantAMO: number;
  montantAMC: number;
  montantPatient: number;
  count_paye: number;
  count_partiel: number;
  count_impaye: number;
  count_degrade: number;
  count_tlc: number;
  count_visite: number;
  count_papier: number;
  count_cabinet: number;
}

export interface PaiementsSums {
  montantCB: number;
  montantESP: number;
  montantCHQ: number;
  montantVIR: number ;
  montantAMO: number ;
  montantAMC: number;
  montantFREE
  CB:Integer;
  ESPECES:Integer;
  CHEQUE:Integer;
  VIREMENT:Integer;
  AMO:Integer;
  AMC:Integer;
  OFFERT:Integer;
}

@Injectable({
  providedIn: 'root'
})
export class RecettesService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders()
  };

  private apiUri = environment.basePath + '/recettes/';

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async create(facture: FactureCreate): Promise<string> {
    try {
      const res = await this._http.post<string>(this.apiUri + 'factures', facture, this.httpOptions).toPromise();
      if (res.status === 201) {
        if (!res.body) {
          throw new Error('201 sans body sur creation recette (ID attendu)');
        }
        return res.body; // UUID
      }
      // Autres 2xx exotiques
      throw new Error(`Statut HTTP inattendu sur creation recette : ${res.status}`);
    } catch (error) {
      this._snack.openSnackBarError(
        error?.error?.message ||
        error?.message ||
        'Erreur lors de la création de la recette HMO'
      );
      throw error;
    }
  }

  public async deleteFacture(idfacture: string): Promise<void> {
    // Si tu veux réutiliser observe: 'response' + withCredentials
    const options = { ...this.httpOptions };

    try {
      await this._http.delete<void>(`${this.apiUri}factures/${idfacture}`, options).toPromise();
      // 204 attendu, pas de body
    } catch (error: any) {
      this._snack.openSnackBar(error?.error?.message || 'Erreur lors de la suppression de la facture');
      throw error;
    }
  }

  public findPaginated(
    filtre: Filtres = {
      perpage: 50,
      currentPage: 1,
      sortBy: 'dateFacture',
      sortOrder: SortEnum.ASC
    },
    idProfil?: string,
    needle?: string,
    range?: { debut?: string | Date; fin?: string | Date },
    flags: {count?: boolean, sums?: boolean} = {count: true, sums: true},
    idPatient?: string,
  ): Observable<{ items: FacturesList[], total: number, sums: FactureSums }> {

    const take = filtre.perpage;
    const skip = take * (filtre.currentPage - 1);

    let headers = new HttpHeaders()
      .set('x-per-page', filtre.perpage.toString())
      .set('x-page', filtre.currentPage.toString())
      .set('x-sort-attribut', filtre.sortBy)
      .set('x-sort', filtre.sortOrder);

    if (needle?.trim()) {
      headers = headers.set('x-find', needle.trim());
    }

    // Build query params for backend filters (id_profil, debut, fin)
    let params = new HttpParams();
    if (idProfil) params = params.set('id', idProfil);
    if (range?.debut) params = params.set('debut', this.toYMD(range.debut));
    if (range?.fin)   params = params.set('fin',   this.toYMD(range.fin));
    if (!flags.count) params = params.set('count', 'false');
    if (!flags.sums)  params = params.set('sums', 'false');
    if (idPatient)   params = params.set('patient', idPatient);

    return this._http.get<{ items: FacturesList[]; total: number; sums: FactureSums }>(
      `${this.apiUri}factures/paginated`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Erreur API:', error);
        return throwError(() => error);
      })
    );
  }

  public findPaiementsPaginated(
    filtre: Filtres = {
      perpage: 50,
      currentPage: 1,
      sortBy: 'datePaiement',
      sortOrder: SortEnum.ASC
    },
    idProfil?: string,
    needle?: string,
    range?: { debut?: string | Date; fin?: string | Date },
    flags: {count?: boolean, sums?: boolean, type?: string} = {count: true, sums: true, type: ''}
  ): Observable<{ items: PaiementItem[], total: number, sums: PaiementsSums }> {

    const take = filtre.perpage;
    const skip = take * (filtre.currentPage - 1);

    let headers = new HttpHeaders()
      .set('x-per-page', filtre.perpage.toString())
      .set('x-page', filtre.currentPage.toString())
      .set('x-sort-attribut', filtre.sortBy)
      .set('x-sort', filtre.sortOrder);

    if (needle?.trim()) {
      headers = headers.set('x-find', needle.trim());
    }

    // Build query params for backend filters (id_profil, debut, fin)
    let params = new HttpParams();
    if (idProfil) params = params.set('id', idProfil);
    if (range?.debut) params = params.set('debut', this.toYMD(range.debut));
    if (range?.fin)   params = params.set('fin',   this.toYMD(range.fin));
    if (!flags.count) params = params.set('count', 'false');
    if (!flags.sums)  params = params.set('sums', 'false');
    if (flags.type)   params = params.set('type', flags.type);

    return this._http.get<{ items: PaiementItem[]; total: number; sums: PaiementsSums }>(
      `${this.apiUri}factures/paiements/paginated`,
      { headers, params }
    ).pipe(
      catchError(error => {
        console.error('Erreur API:', error);
        return throwError(() => error);
      })
    );
  }

  public async addPaiement(idfacture: string, payload: AddPaiementRequest, idemKey?: string): Promise<any> {
    try {

      const key = (idemKey && idemKey.length >= 8) ? idemKey : this.newIdemKey();
      let httpOptions = {...this.httpOptions};
      httpOptions.headers = httpOptions.headers.set('idempotency-key', key);

      const res = await this._http.post<AddPaiementResponse>(`${this.apiUri}factures/${idfacture}/paiements/`, payload, httpOptions).toPromise();
      const replayHeader = String(res.headers.get('idempotent-Replayed') || '').toLowerCase();
      const idemHeader = res.headers.get('idempotency-Key') || key;
      const body = res.body as AddPaiementResponse;
      return {
        ...body,
        idempotencyKey: idemHeader,
        idempotentReplayed: replayHeader === 'true'
      };
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors de l\'ajout du paiement');
      throw error;
    }
  }

  public async listPaiements(idfacture: string, duPatient: boolean = false): Promise<ListPaiementsResponse> {
    try {
      const params = new HttpParams().set('duPatient', duPatient ? 'true' : 'false');
      const res = await this._http.get<ListPaiementsResponse>(`${this.apiUri}factures/${idfacture}/paiements`, { ...this.httpOptions, params }).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors du chargement des paiements');
      throw error;
    }
  }

  public async deletePaiement(idfacture: string, paiementId: string): Promise<FactureMeta> {
    try {
      const res = await this._http.delete<FactureMeta>(`${this.apiUri}factures/${idfacture}/paiements/${paiementId}`, this.httpOptions).toPromise();
      return res.body;
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors de la suppression du paiement');
      throw error;
    }
  }

  public async reassignSingleFactureAndPatientPayments(idfacture: string, newIdPatient: string): Promise<Boolean> {
    try {
      const res = await this._http.patch<{ success: boolean }>(`${this.apiUri}factures/${idfacture}/reassign-patient/${newIdPatient}`, this.httpOptions).toPromise();
      return res?.success === true;
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors de la réaffectation des factures et paiements');
      throw error;
    }
  }

  public async pointerRemiseBanqueBatch(ids: string[], dateRemise: Date): Promise<void> {
    const options = { ...this.httpOptions };
    // on convertit en string ISO (propre pour le backend)
    const payload = {
      ids,
      remiseBanque: true,
      dateRemise: dateRemise.toISOString().slice(0, 10), // YYYY-MM-DD
    };
    try {
      const res = await this._http.patch<void>(`${this.apiUri}factures/paiements/remise-banque`, payload, options).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors de la mise à jour des paiements');
      throw error;
    }
  }

  public async annulerRemiseBanque(id: string): Promise<void> {
    try {
      const options = { ...this.httpOptions };
      const res = await this._http.patch<void>(`${this.apiUri}factures/paiements/${id}/remise-banque/annuler`, null, options).toPromise();
      return;
    } catch (error) {
      this._snack.openSnackBar(error.error.message || 'Erreur lors de la mise à jour des paiements');
      throw error;
    }
  }
  /** HELPERS */
  private toYMD(d: string | Date): string {
    if (typeof d === 'string') return d; // suppose déjà YYYY-MM-DD
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
  private newIdemKey(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return (crypto as any).randomUUID();
    }
    // fallback
    const s4 = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    return `${s4()}${s4()}-${s4()}-${s4()}-${s4()}-${s4()}${s4()}${s4()}`;
  }
}
